import React, { createContext, useContext, useEffect } from 'react';
import { useTenant } from './useTenant';
import { useCollection } from './hooks/useCollection';
import { ensureTenant } from './db';
import {
  ASSET_TYPES, ASSET_STATUS, LOCATION_TYPES, MOVEMENT_TYPES, SENSOR_CATALOG,
} from '../data/catalogs';

const AppDataContext = createContext(null);

// Singleton di lookup: aggiornato dal provider per gli helper puri
// (assetHelpers.js, components/index.js) che non possono usare hook.
let _lookupData = null;
export const getLookupData = () => _lookupData;

export function AppDataProvider({ children }) {
  const { activeTenantId, emailVerified } = useTenant();
  const tid = activeTenantId;

  // Attacca i listener del tenant (onSnapshot su Firestore) SOLO a email verificata:
  // le regole Firestore richiedono email_verified per i dati del tenant, e in
  // registrazione il tenantId è impostato prima della verifica. Quando la verifica
  // avviene, `emailVerified` passa a true e l'effetto ri-attacca col token aggiornato.
  useEffect(() => { if (tid && emailVerified) ensureTenant(tid); }, [tid, emailVerified]);

  const ASSET_DATA       = useCollection(tid, 'assets');
  const LOCATIONS_DATA   = useCollection(tid, 'locations');
  const SENSOR_INSTANCES = useCollection(tid, 'sensorInstances');
  const MOVEMENTS_DATA   = useCollection(tid, 'movements');

  const value = {
    loading: false,
    ASSET_DATA,
    LOCATIONS_DATA,
    SENSOR_INSTANCES,
    MOVEMENTS_DATA,
    // Cataloghi statici bundlati
    ASSET_TYPES,
    ASSET_STATUS,
    LOCATION_TYPES,
    MOVEMENT_TYPES,
    SENSOR_CATALOG,
  };

  // Aggiornato IN RENDER (non in un effect post-commit): così gli helper puri
  // che leggono getLookupData() durante lo stesso render vedono i dati correnti,
  // senza il lag di un frame. Assegnazione idempotente, sicura.
  _lookupData = value;

  return (
    <AppDataContext.Provider value={value}>
      {children}
    </AppDataContext.Provider>
  );
}

export function useAppData() {
  const ctx = useContext(AppDataContext);
  if (!ctx) throw new Error('useAppData deve essere usato dentro <AppDataProvider>');
  return ctx;
}
